import { useRef, useState } from "react";
import { MapPin, X, Flask } from "@phosphor-icons/react";
import api from "../lib/api";

export const PlaceSearch = ({ onSelect, selected, onClear }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [source, setSource] = useState(null);
  const [searching, setSearching] = useState(false);
  const token = useRef(crypto.randomUUID());
  const timer = useRef(null);

  const search = (value) => {
    setQ(value);
    window.clearTimeout(timer.current);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.post("/admin/places/autocomplete", {
          input: value,
          session_token: token.current,
        });
        setResults(data.suggestions || []);
        setSource(data.source);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const choose = (s) => {
    const pred = s.placePrediction || {};
    onSelect({ place_id: pred.placeId, label: pred.text?.text || "" });
    setResults([]);
    setQ("");
    token.current = crypto.randomUUID();
  };

  if (selected) {
    return (
      <div
        data-testid="place-selected"
        className="flex items-center justify-between gap-3 rounded-2xl border border-pine/30 bg-pine-light/60 px-4 py-3"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <MapPin size={20} weight="fill" className="shrink-0 text-pine" />
          <span className="truncate text-sm font-semibold text-ink">{selected.label}</span>
        </div>
        <button
          type="button"
          data-testid="place-clear-button"
          onClick={onClear}
          aria-label="Clear selected business"
          className="shrink-0 text-soot transition-colors hover:text-ink"
        >
          <X size={18} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => search(e.target.value)}
        data-testid="place-search-input"
        className="field-input"
        placeholder="Start typing your business name…"
        aria-label="Search for your business"
        autoComplete="off"
      />
      {source === "sample" && (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-700">
          <Flask size={14} weight="duotone" />
          Demo search results — live Google search activates once the API key is configured.
        </p>
      )}
      {searching && <p className="mt-2 text-xs text-soot">Searching…</p>}
      {results.length > 0 && (
        <ul
          data-testid="place-suggestions"
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-pine-soft bg-white shadow-[0_16px_40px_rgb(0,0,0,0.12)]"
        >
          {results.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                data-testid={`place-suggestion-${i}`}
                onClick={() => choose(s)}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-pine-light/50"
              >
                <MapPin size={16} weight="duotone" className="shrink-0 text-pine" />
                {s.placePrediction?.text?.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
